import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getDashboardStats, getDetailedStats, getDetailedStatsByDate } from '../lib/api';
import { 
  Users, Package, ShoppingCart, Receipt, TrendingUp, Euro, 
  Calendar, UserCircle, Truck, Wallet, CalendarDays, X
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6', '#EC4899'];
const PERIODO_OPTIONS = [
  { label: '7 giorni', value: 7 },
  { label: '1 mese', value: 30 },
  { label: '3 mesi', value: 90 },
];

const StatCard = ({ title, value, icon: Icon, color = 'primary', isCurrency = false, subtitle }) => (
  <Card className="hover:border-primary/50 transition-colors">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={`text-2xl font-bold font-mono mt-1`}>
            {isCurrency ? `€${value.toFixed(2)}` : value}
          </p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-${color}/10`}>
          <Icon className={`h-6 w-6 text-${color}`} strokeWidth={1.5} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DetailCard = ({ title, items, icon: Icon, emptyText, color = 'primary' }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-lg flex items-center gap-2">
        <Icon className={`h-5 w-5 text-${color}`} strokeWidth={1.5} />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
              <div>
                <p className="font-medium">{item.nome}</p>
                <p className="text-xs text-muted-foreground">{item.count} operazioni</p>
              </div>
              <p className="font-mono font-bold text-lg">€{item.totale.toFixed(2)}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-center py-4">{emptyText}</p>
      )}
    </CardContent>
  </Card>
);

// Componente per visualizzare i dettagli di un singolo giorno
const DayDetailView = ({ data, dateStr }) => {
  if (!data) return null;
  
  const chiusura = data.chiusura;
  const spese = data.spese || [];
  const speseTotal = spese.reduce((acc, s) => acc + s.importo, 0);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-xl font-bold">
        <CalendarDays className="h-6 w-6 text-primary" />
        Dettagli del {new Date(dateStr).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </div>
      
      {/* Chiusura Fiscale del giorno */}
      {chiusura ? (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              Chiusura Fiscale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-primary/10 text-center">
                <p className="text-xs text-muted-foreground">Totale Fiscale</p>
                <p className="text-xl font-mono font-bold text-primary">€{chiusura.totale_fiscale?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10 text-center">
                <p className="text-xs text-muted-foreground">Totale Completo</p>
                <p className="text-xl font-mono font-bold text-blue-500">€{chiusura.totale_completo?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10 text-center">
                <p className="text-xs text-muted-foreground">Contanti Extra</p>
                <p className="text-xl font-mono font-bold text-green-500">€{chiusura.contanti_extra?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10 text-center">
                <p className="text-xs text-muted-foreground">Fondo Cassa</p>
                <p className="text-xl font-mono font-bold text-amber-500">€{chiusura.fondo_cassa?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
            {chiusura.note && (
              <p className="mt-4 text-sm text-muted-foreground italic">Note: {chiusura.note}</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-muted-foreground">
            <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
            Nessuna chiusura fiscale per questo giorno
          </CardContent>
        </Card>
      )}
      
      {/* Spese del giorno */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-amber-500" />
              Spese del Giorno
            </span>
            <span className="text-sm font-normal text-muted-foreground">
              Totale: <span className="font-mono font-bold text-foreground">€{speseTotal.toFixed(2)}</span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {spese.length > 0 ? (
            <div className="space-y-2">
              {spese.map((spesa, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div>
                    <p className="font-medium">{spesa.descrizione}</p>
                    <p className="text-xs text-muted-foreground">
                      {spesa.categoria}
                      {spesa.persona && ` • ${spesa.persona}`}
                      {spesa.fornitore_nome && ` • ${spesa.fornitore_nome}`}
                    </p>
                  </div>
                  <p className="font-mono font-bold">€{spesa.importo.toFixed(2)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">Nessuna spesa registrata</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(30);
  const [customMode, setCustomMode] = useState(false);
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [singleDayMode, setSingleDayMode] = useState(false);
  const [singleDayData, setSingleDayData] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (!customMode && !singleDayMode) {
      loadDetailedStats();
    }
  }, [periodo, customMode, singleDayMode]);

  const loadStats = async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedStats = async () => {
    try {
      const data = await getDetailedStats(periodo);
      setDetailedStats(data);
    } catch (error) {
      console.error('Error loading detailed stats:', error);
    }
  };

  const loadCustomRangeStats = async () => {
    if (!customDateStart || !customDateEnd) return;
    
    try {
      const data = await getDetailedStatsByDate(customDateStart, customDateEnd);
      setDetailedStats(data);
      setSingleDayMode(false);
      setSingleDayData(null);
    } catch (error) {
      console.error('Error loading custom stats:', error);
    }
  };

  const loadSingleDayStats = async () => {
    if (!customDateStart) return;
    
    try {
      const data = await getDetailedStatsByDate(customDateStart, customDateStart);
      setSingleDayData(data);
      setSingleDayMode(true);
    } catch (error) {
      console.error('Error loading single day stats:', error);
    }
  };

  const handlePeriodoClick = (value) => {
    setPeriodo(value);
    setCustomMode(false);
    setSingleDayMode(false);
    setSingleDayData(null);
  };

  const handleCustomClick = () => {
    setCustomMode(true);
    setSingleDayMode(false);
    setSingleDayData(null);
    // Set default dates to today
    const today = new Date().toISOString().split('T')[0];
    setCustomDateStart(today);
    setCustomDateEnd(today);
  };

  const resetCustomMode = () => {
    setCustomMode(false);
    setSingleDayMode(false);
    setSingleDayData(null);
    setCustomDateStart('');
    setCustomDateEnd('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Prepara dati per i grafici
  const speseChartData = detailedStats?.spese?.per_categoria 
    ? Object.entries(detailedStats.spese.per_categoria)
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value
        }))
    : [];

  const personaleData = detailedStats?.spese?.per_personale
    ? Object.entries(detailedStats.spese.per_personale)
        .map(([nome, data]) => ({ nome, ...data }))
        .sort((a, b) => b.totale - a.totale)
    : [];

  const fornitoreData = detailedStats?.spese?.per_fornitore
    ? Object.entries(detailedStats.spese.per_fornitore)
        .map(([nome, data]) => ({ nome, ...data }))
        .sort((a, b) => b.totale - a.totale)
    : [];

  const chiusureTrend = detailedStats?.chiusure?.trend || [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Panoramica del tuo bar bistrot</p>
        </div>
        
        {/* Periodo Selector */}
        <div className="flex gap-2 flex-wrap items-center">
          {PERIODO_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              variant={!customMode && !singleDayMode && periodo === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePeriodoClick(opt.value)}
              data-testid={`periodo-${opt.value}`}
            >
              <Calendar className="h-4 w-4 mr-1" />
              {opt.label}
            </Button>
          ))}
          <Button
            variant={customMode || singleDayMode ? 'default' : 'outline'}
            size="sm"
            onClick={handleCustomClick}
            data-testid="periodo-custom"
          >
            <CalendarDays className="h-4 w-4 mr-1" />
            Personalizzato
          </Button>
        </div>
      </div>

      {/* Custom Date Picker */}
      {(customMode || singleDayMode) && (
        <Card className="border-primary/30">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor="dateStart" className="text-sm">Data Inizio / Giorno Singolo</Label>
                <Input
                  id="dateStart"
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="dateEnd" className="text-sm">Data Fine (opzionale per range)</Label>
                <Input
                  id="dateEnd"
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={loadSingleDayStats} variant="outline" size="sm">
                  <CalendarDays className="h-4 w-4 mr-1" />
                  Vedi Giorno
                </Button>
                <Button onClick={loadCustomRangeStats} size="sm">
                  <Calendar className="h-4 w-4 mr-1" />
                  Vedi Periodo
                </Button>
                <Button onClick={resetCustomMode} variant="ghost" size="sm">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vista Giorno Singolo */}
      {singleDayMode && singleDayData && (
        <DayDetailView data={singleDayData} dateStr={customDateStart} />
      )}

      {/* Vista Normale (periodo) */}
      {!singleDayMode && (
        <>
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Fornitori" 
              value={stats?.fornitori_count || 0} 
              icon={Users}
            />
            <StatCard 
              title="Articoli" 
              value={stats?.articoli_count || 0} 
              icon={Package}
            />
            <StatCard 
              title="Ordini nel Periodo" 
              value={detailedStats?.ordini?.count || 0} 
              icon={ShoppingCart}
              subtitle={`Totale: €${(detailedStats?.ordini?.totale || 0).toFixed(2)}`}
            />
            <StatCard 
              title="Spese nel Periodo" 
              value={detailedStats?.spese?.totale || 0} 
              icon={Receipt}
              isCurrency
            />
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-2xl bg-primary/20">
                    <TrendingUp className="h-8 w-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Totale Fiscale</p>
                    <p className="text-3xl font-bold font-mono text-primary">
                      €{(detailedStats?.chiusure?.totale_fiscale || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {detailedStats?.chiusure?.count || 0} chiusure
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-2xl bg-blue-500/20">
                    <Euro className="h-8 w-8 text-blue-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Totale Completo</p>
                    <p className="text-3xl font-bold font-mono text-blue-500">
                      €{(detailedStats?.chiusure?.totale_completo || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-2xl bg-green-500/20">
                    <Wallet className="h-8 w-8 text-green-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Contanti Extra</p>
                    <p className="text-3xl font-bold font-mono text-green-500">
                      €{(detailedStats?.chiusure?.contanti_extra_totale || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Stats - Personale & Fornitori */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DetailCard
              title="Spese per Dipendente"
              items={personaleData}
              icon={UserCircle}
              emptyText="Nessuna spesa per personale nel periodo"
              color="amber-500"
            />
            <DetailCard
              title="Spese per Fornitore"
              items={fornitoreData}
              icon={Truck}
              emptyText="Nessuna spesa per fornitori nel periodo"
              color="blue-500"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Spese per Categoria */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Spese per Categoria</CardTitle>
              </CardHeader>
              <CardContent>
                {speseChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={speseChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {speseChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => `€${value.toFixed(2)}`}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    Nessuna spesa nel periodo selezionato
                  </div>
                )}
                <div className="flex flex-wrap gap-4 justify-center mt-4">
                  {speseChartData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {entry.name}: €{entry.value.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Andamento Incassi */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Andamento Incassi</CardTitle>
              </CardHeader>
              <CardContent>
                {chiusureTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={chiusureTrend}>
                      <XAxis 
                        dataKey="data" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickFormatter={(value) => value.split('-').slice(1).join('/')}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickFormatter={(value) => `€${value}`}
                      />
                      <Tooltip 
                        formatter={(value) => `€${value.toFixed(2)}`}
                        labelFormatter={(label) => `Data: ${label}`}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="totale_fiscale" 
                        name="Fiscale"
                        stroke="#F59E0B" 
                        strokeWidth={2}
                        dot={{ fill: '#F59E0B' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="contanti_extra" 
                        name="Extra"
                        stroke="#10B981" 
                        strokeWidth={2}
                        dot={{ fill: '#10B981' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    Nessuna chiusura nel periodo selezionato
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Table per Categoria */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Riepilogo Spese per Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(detailedStats?.spese?.per_categoria || {}).map(([cat, value]) => (
                  <div key={cat} className="p-4 rounded-lg bg-secondary/50 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{cat}</p>
                    <p className="text-2xl font-mono font-bold mt-1">€{value.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;
